import React from 'react';
import { colors, fonts, sectionLabelStyle } from './styles';
import { AIResearcherDemo } from './AIResearcherDemo';
import { Highlight } from './Highlight';

const stepShape = 'polygon(15% 0%, 100% 0%, 85% 100%, 0% 100%)';

const steps = [
  {
    n: 1,
    badge: 'AI-assisted intake',
    title: 'Your team identifies a need',
    body: "When patient insights are needed for a decision, a strategy question, or a hypothesis your team wants to pressure-test, that's where we start. Our AI Research Assistant engages your team in a live, conversational experience to refine the need into a clear, answerable inquiry. This is often what surprises clients most.",
  },
  {
    n: 2,
    badge: 'Verified community',
    title: 'We conduct the Pulse Check™',
    body: 'We activate the right patients from our verified community — targeted by condition, treatment stage, care pathway, and geography. We draw from existing patient intelligence or field fresh input, depending on what your question requires.',
  },
  {
    n: 3,
    badge: 'Results in days',
    title: 'You receive patient intelligence',
    body: 'Themes, patterns, and rich contextual patient voice — delivered back to your team in days. Every Pulse Check builds on the last, compounding your intelligence over time.',
  },
];

export const HowItWorksSection = () => {
  return (
    <section
      style={{
        backgroundColor: colors.white,
        padding: '96px 48px',
        boxSizing: 'border-box',
      }}
    >
      <style>{`
        @media (max-width: 768px) {
          .pi-how-wrap { padding-left: 24px !important; padding-right: 24px !important; padding-top: 64px !important; padding-bottom: 64px !important; }
          .pi-steps { grid-template-columns: 1fr !important; gap: 32px !important; }
          .pi-steps-line { display: none !important; }
          .pi-demo { padding: 40px 24px !important; }
        }
      `}</style>
      <div className="pi-how-wrap" style={{ maxWidth: '1100px', margin: '0 auto' }}>
        <div style={{ maxWidth: '760px', margin: '0 auto 56px', textAlign: 'center' }}>
          <p style={{ ...sectionLabelStyle, textAlign: 'center' }}>How it works</p>
          <h2
            style={{
              fontFamily: fonts.serif,
              fontSize: 'clamp(26px, 3vw, 40px)',
              fontWeight: 700,
              color: colors.charcoal,
              margin: '0 0 16px',
              lineHeight: 1.2,
            }}
          >
            Three steps.
            <br />
            <span style={{ fontStyle: 'italic', fontWeight: 400 }}>
              <Highlight>Real</Highlight> answers.
            </span>
          </h2>
          <p
            style={{
              fontFamily: fonts.sans,
              fontSize: '17px',
              color: colors.warmGray,
              maxWidth: '600px',
              margin: '0 auto',
              fontWeight: 400,
              lineHeight: 1.65,
            }}
          >
            A decade of experience helping clients understand patient experience means you get expert-level insights — starting with the very first conversation.
          </p>
        </div>

        <div
          className="pi-steps"
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: 0,
            position: 'relative',
          }}
        >
          {steps.map((s) => (
            <div key={s.n} style={{ padding: '0 24px', textAlign: 'center' }}>
              <div
                style={{
                  width: '72px',
                  height: '60px',
                  clipPath: stepShape,
                  background: `linear-gradient(135deg, ${colors.coral} 0%, ${colors.peach} 100%)`,
                  color: colors.white,
                  fontFamily: fonts.serif,
                  fontSize: '24px',
                  fontWeight: 900,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: '20px',
                  marginLeft: 'auto',
                  marginRight: 'auto',
                  position: 'relative',
                  zIndex: 1,
                }}
              >
                {s.n}
              </div>
              <div
                style={{
                  display: 'inline-block',
                  backgroundColor: colors.coralLight,
                  color: colors.coral,
                  fontFamily: fonts.sans,
                  fontSize: '11px',
                  fontWeight: 500,
                  letterSpacing: '0.3px',
                  padding: '4px 10px',
                  borderRadius: '20px',
                  marginBottom: '12px',
                }}
              >
                {s.badge}
              </div>
              <div
                style={{
                  fontFamily: fonts.sans,
                  fontSize: '17px',
                  fontWeight: 500,
                  color: colors.dark,
                  marginBottom: '10px',
                  lineHeight: 1.3,
                }}
              >
                {s.title}
              </div>
              <div
                style={{
                  fontFamily: fonts.sans,
                  fontSize: '15px',
                  color: colors.warmGray,
                  lineHeight: 1.65,
                  fontWeight: 300,
                }}
              >
                {s.body}
              </div>
            </div>
          ))}
        </div>

        <div style={{ marginTop: '72px' }}>
          <div style={{ textAlign: 'center', marginBottom: '32px' }}>
            <span
              style={{
                fontFamily: fonts.sans,
                fontSize: '11px',
                fontWeight: 700,
                letterSpacing: '2px',
                textTransform: 'uppercase',
                color: colors.deepCoral,
                display: 'inline-flex',
                alignItems: 'center',
                gap: '10px',
              }}
            >
              <span
                aria-hidden="true"
                style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  backgroundColor: colors.terracotta,
                  display: 'inline-block',
                  boxShadow: `0 0 0 0 ${colors.terracotta}`,
                  animation: 'pi-demo-pulse 2s infinite',
                }}
              />
              Live demo
            </span>
            <style>{`
              @keyframes pi-demo-pulse {
                0% { box-shadow: 0 0 0 0 rgba(232,146,124,0.55); }
                70% { box-shadow: 0 0 0 10px rgba(232,146,124,0); }
                100% { box-shadow: 0 0 0 0 rgba(232,146,124,0); }
              }
            `}</style>
          </div>
          <AIResearcherDemo />
        </div>
      </div>
    </section>
  );
};
